import { proxy } from 'valtio';
import { http } from '../api';

export interface AIModelConfigItem {
  name: string;
  desc?: string;
  key: string;
  model?: string;
  defaultUpscale?: string,
  defaultImageSize?: string;
  defaultAspectRatio?: string;
  defaultQuality?: string;
  defaultDuration?: number;
  switchPriority?: number;
  defaultimagesize?: string;
  defaultaspectratio?: string;
  switchpriority?: number;
  param?: Readonly<Record<string, any>>;
  [key: string]: unknown;
}
export interface AIModelRefItem {
  name: string;
  vip: number;
  key: string;
}

export type TaskModelKind = 'text' | 'image' | 'video';
export type TextTaskModelRefKey = 'text_generation' | 'text_list';

export type TaskModelBucketData =
  | readonly AIModelConfigItem[]
  | Record<string, AIModelConfigItem>
  | Readonly<Record<string, AIModelConfigItem>>;

export interface RawTaskModelListData {
  video: Record<string, AIModelConfigItem>;
  image: Record<string, AIModelConfigItem>;
  text: Record<string, AIModelConfigItem>;
}

export type TaskModelRefData = Record<string, readonly AIModelConfigItem[]>;

export interface TaskModelData {
  model_list: RawTaskModelListData;
  model_ref: TaskModelRefData;
  point_rule?: Record<string, string>;
}

export interface ResolvedTaskModelItem extends AIModelConfigItem {
  requestValue: string;
  sourceKind: TaskModelKind;
  refBucket?: string;
  rawBucketId?: string;
}

export const OP_KEYS = {
  "f2vi": "f2vi", // 首尾帧视频
  "i2vi": "i2vi", //图生视频
  'r2vi': 'r2vi', // 全能参考视频
  't2vi': 't2vi', // 文生视频

  'image': 'image', // 文生图
  'panorama': 'panorama', //全景图
  'refine': 'refine', //产品精修
  'text_generation': 'text_generation', //生文本
  'text_list': 'text_list', //提取为文本列表
  'us': 'us', //高清修复
} as const
export type ENUM_OP_KEYS = (typeof OP_KEYS)[keyof typeof OP_KEYS];

export const taskConfigStore = proxy<{
  rawModelConfig: RawTaskModelListData;
  modelRef: { [k in ENUM_OP_KEYS]: AIModelRefItem[] };
  keyMap: {
    [k: string]: {
      ref: AIModelRefItem,
      cfg: AIModelConfigItem,
    }
  };
  pointRule: Record<string, string>;
  checkRule: Record<string, Record<string, string>>;
}>({
  rawModelConfig: null as any,
  modelRef: null as any,
  keyMap: {} as any,
  pointRule: {},
  checkRule: {},
});

export async function fetchTaskModel() {
  try {
    const response = await http.post<TaskModelData>('/zs/AiCanvasTask/getTaskModel');
    if (response.code === 1 && response.data) {
      const mapList = Object.entries(response.data.model_list).map(item => Object.entries(item[1] as Record<string, AIModelConfigItem>)).flat()
      const modelMap: { [k: string]: AIModelConfigItem } = {};
      mapList.forEach(item => {
        modelMap[item[0]] = item[1];
      })
      taskConfigStore.rawModelConfig = response.data.model_list;
      taskConfigStore.modelRef = response.data.model_ref as any;
      taskConfigStore.pointRule = response.data.point_rule || {};
      taskConfigStore.keyMap = {};
      Object.entries(response.data.model_ref).forEach(entry => {
        entry[1].forEach(item => {
          taskConfigStore.keyMap[item.key] = {
            ref: item as any,
            cfg: modelMap[item.key],
          };
        })
      })
      return true;
    }
  } catch (error) {
    console.log('[TaskConfigEntity] Failed to fetch task model:', error);
  }
  return false;
}

export function getAllModels(type: ENUM_OP_KEYS): AIModelRefItem[] {
  const modeModelRefs = (taskConfigStore.modelRef?.[type] || []) as AIModelRefItem[];
  return modeModelRefs
}

export function getModelConfig(type: 'image' | 'video' | 'text', key: string): AIModelConfigItem {
  const model = (taskConfigStore.rawModelConfig?.[type]?.[key] || {}) as AIModelConfigItem;
  return model
}

export function getValidModelKey(type: ENUM_OP_KEYS, key: string) {
  // taskConfigStore.checkRule = {
  //   "f2vi": {
  //     "ssss": "seedance1-5"
  //   },
  //   "image": {
  //     "ssss": "banana-pro"
  //   },
  //   "text": {
  //     "ssss": "gemini-3.1-flash-lite"
  //   },
  //   "panorama": {
  //     "ssss": "panorama_banana_pro"
  //   },
  // }
  return taskConfigStore.checkRule[type]?.[key] || '';
}

function readTaskModelString(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

export function resolveTaskModelItems(
  bucket: TaskModelBucketData | null | undefined,
): AIModelConfigItem[] {
  if (!bucket) {
    return [];
  }

  const items = Array.isArray(bucket) ? bucket : Object.values(bucket);
  return items.map((item) => normalizeTaskModelItem(item));
}

export function resolveTaskModelPriority(
  item: Pick<AIModelConfigItem, 'switchPriority' | 'switchpriority'> | null | undefined,
): number | undefined {
  if (typeof item?.switchPriority === 'number') {
    return item.switchPriority;
  }

  return typeof item?.switchpriority === 'number' ? item.switchpriority : undefined;
}

export function normalizeTaskModelItem(
  item: AIModelConfigItem | null | undefined,
): AIModelConfigItem {
  const normalized = item ? { ...item } : { name: '', key: '' };
  return {
    ...normalized,
    defaultImageSize: readTaskModelString(normalized.defaultImageSize)
      || readTaskModelString(normalized.defaultimagesize),
    defaultAspectRatio: readTaskModelString(normalized.defaultAspectRatio)
      || readTaskModelString(normalized.defaultaspectratio),
    switchPriority: resolveTaskModelPriority(normalized),
  };
}